import React, { useEffect, useId, useRef, useState } from 'react';
import { useMergePlan, useMergeListings, useUndoListingMerge } from '../hooks/useListingMerge';
import RetryCountdown from './RetryCountdown';
import { formatDateTimeEastern } from '../utils/dates';
import {
  UNKNOWN_OUTCOME_TEXT, classifyMergeError, runMergeGroups, isRunnable, canUndo, undoneResult,
  sessionChanges, planParts, runSelection, displayGroups, planErrorText, groupLabel, sourcesLine,
  blockedLabel, tagList,
} from '../utils/mergeRun';

// Compare → "Merge listings": the server's plan of side-by-side listings (same
// section and row) that can become one Skybox listing each. Checked groups
// merge one at a time, each with its own result; a 429, a 503 "not ready" or
// an unknown outcome stops the run, and so does closing the window
// (utils/mergeRun). Every merge done here that the server recorded, and each
// recent one, has an Undo. Same shell as BulkPriceModal; the page re-reads
// the plan and the event's pricing when it closes.
//
// Props: eventId, onClose({ changed, consumedIds }) — consumedIds are listing
//        ids (strings) that are gone, or may be, after this session;
//        returnFocusRef (optional) — the element focus goes back to on close.

const RULES_NOTE = 'Same section and row, seats side by side, same tags and notes, max 8 seats. '
  + 'The merged listing keeps the highest B; unpriced listings merge too. '
  + 'New listings need Lion Auto approval; L shows once Vivid lists them.';

const idList = (ids) => ids.filter(id => id != null && id !== '').map(id => `#${id}`).join(', ');
const shortLabel = (x) => `Sec ${x.section ?? '-'} · Row ${x.row ?? '-'} · ${x.seats ?? '-'}`;

function WarningList({ warnings }) {
  if (!warnings?.length) return null;
  return <ul className="merge-warnings">{warnings.map((w, i) => <li key={`${i}:${w}`}>{w}</li>)}</ul>;
}

// An undo's state, in words, with its button while one can be tried
function UndoControl({ undo, label, disabled, onUndo }) {
  if (undo?.status === 'undoing') return <span className="merge-status busy">undoing…</span>;
  if (undo?.status === 'undone') {
    return (
      <>
        <span className="merge-status ok">
          ✓ Undone{undo.restoredIds.length ? ` — restored as ${idList(undo.restoredIds)}` : ''}
        </span>
        <WarningList warnings={undo.warnings} />
      </>
    );
  }
  if (undo?.status === 'unknown') return <span className="merge-status warn">⚠ {undo.error}</span>;
  return (
    <span className="merge-undo">
      <button type="button" className="btn-secondary btn-sm" onClick={onUndo} disabled={disabled} aria-label={`Undo merge of ${label}`}>
        Undo
      </button>
      {undo?.error && (
        <span className={`merge-status ${undo.status === 'unavailable' ? 'warn' : 'bad'}`}>
          {undo.status === 'unavailable' ? '' : '✗ '}{undo.error}
          {undo.retryAt && <> <RetryCountdown until={undo.retryAt} /></>}
        </span>
      )}
    </span>
  );
}

// One group's run result (nothing before it was tried)
function GroupResult({ result, children }) {
  if (!result) return null;
  switch (result.status) {
    case 'merging':
      return <span className="merge-status busy">merging…</span>;
    case 'merged':
      return (
        <>
          <span className="merge-status ok">✓ new listing {idList(result.mergedIds)}</span>
          <WarningList warnings={result.warnings} />
          {children}
        </>
      );
    case 'unknown':
      return <span className="merge-status warn">⚠ {result.error}</span>;
    case 'unavailable':
      return <span className="merge-status warn">not sent: {result.error}</span>;
    case 'not_sent':
      return <span className="merge-status busy">not sent</span>;
    default:
      return <span className="merge-status bad">✗ {result.error}</span>;
  }
}

function MergeListingsModal({ eventId, onClose, returnFocusRef }) {
  const { data, isLoading, isError, error, isFetching, refetch } = useMergePlan(eventId);
  const mergeMutation = useMergeListings();
  const undoMutation = useUndoListingMerge(eventId);
  const titleId = useId();
  const dialogRef = useRef(null);
  const aliveRef = useRef(true); // false once unmounted: a run sends nothing more
  // Group keys the user unticked: every group starts ticked, even one that
  // only arrives with a later read of the plan
  const [unchecked, setUnchecked] = useState(() => new Set());
  const [ran, setRan] = useState({}); // group key -> the group as it was sent (its row outlives a re-read plan)
  const [results, setResults] = useState({}); // group key -> run result (utils/mergeRun)
  const [undos, setUndos] = useState({}); // String(merge id) -> { status, error?, retryAt?, restoredIds?, warnings?, mergedIds }
  const [stop, setStop] = useState(null); // why the last run stopped: { kind, message, retryAt }
  const [running, setRunning] = useState(false);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Focus the dialog on open; give it back to the opener on close
  useEffect(() => {
    dialogRef.current?.focus();
    return () => returnFocusRef?.current?.focus?.();
  }, [returnFocusRef]);

  const { groups, blocked, recent } = planParts(data);
  const { runnable, selected, allChecked, locked } = runSelection(groups, results, unchecked);
  const busy = running || Object.values(undos).some(u => u.status === 'undoing');
  const resultList = Object.values(results);
  const mergedCount = resultList.filter(r => r.status === 'merged').length;
  const failedCount = resultList.filter(r => r.status === 'failed').length;

  const handleClose = () => {
    if (!busy) onClose(sessionChanges(results, undos));
  };

  const toggle = (key) => setUnchecked(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const toggleAll = () => setUnchecked(allChecked ? new Set(runnable.map(g => g.key)) : new Set());

  const handleMerge = async () => {
    if (!selected.length || busy) return;
    const planEventId = eventId; // every request carries the event this plan was read for
    const batch = selected;
    setRan(prev => ({ ...prev, ...Object.fromEntries(batch.map(g => [g.key, g])) }));
    setRunning(true);
    setStop(null);
    try {
      const out = await runMergeGroups(batch, (payload) => mergeMutation.mutateAsync({ eventId: planEventId, ...payload }), {
        onUpdate: (key, result) => setResults(prev => ({ ...prev, [key]: result })),
        shouldContinue: () => aliveRef.current,
      });
      setStop(out.stop);
    } finally {
      setRunning(false);
    }
  };

  const handleUndo = async (mergeId, mergedIds, label) => {
    const key = String(mergeId);
    if (busy || !canUndo(undos[key])) return;
    const target = mergedIds.length ? ` (listing ${idList(mergedIds)})` : '';
    if (!window.confirm(`Undo the merge of ${label}${target}? Skybox splits it back into the original listings; they come back with new listing ids.`)) return;
    setUndos(prev => ({ ...prev, [key]: { status: 'undoing', mergedIds } }));
    try {
      const body = await undoMutation.mutateAsync(mergeId);
      setUndos(prev => ({ ...prev, [key]: undoneResult(body, mergedIds) }));
    } catch (err) {
      const info = classifyMergeError(err);
      setUndos(prev => ({ ...prev, [key]: { status: info.kind, error: info.message, retryAt: info.retryAt, mergedIds } }));
    }
  };

  const groupRow = ({ group: g, inPlan }) => {
    const result = results[g.key];
    const canRun = inPlan && !locked && isRunnable(result);
    const tags = tagList(g.tags);
    const label = shortLabel(g);
    return (
      <li key={g.key} className="merge-row">
        <input
          type="checkbox"
          checked={canRun && !unchecked.has(g.key)}
          disabled={!canRun || busy}
          onChange={() => toggle(g.key)}
          aria-label={`Merge ${groupLabel(g)}`}
        />
        <div className="merge-row-main">
          <div className="merge-label">
            {groupLabel(g)}
            {g.seatType === 'ALTERNATING' && <span className="merge-badge" title="Odd/even seats (Skybox seat type ALTERNATING)">O/E</span>}
          </div>
          <div className="merge-sources" title={idList((g.sources || []).map(s => s?.id))}>
            {sourcesLine(g)}
            {tags.length > 0 && <span className="merge-tags">tags: {tags.join(', ')}</span>}
          </div>
        </div>
        <div className="merge-result" aria-live="polite">
          <GroupResult result={result}>
            {result?.mergeId != null && (
              <UndoControl
                undo={undos[String(result.mergeId)]}
                label={label}
                disabled={busy}
                onUndo={() => handleUndo(result.mergeId, result.mergedIds, label)}
              />
            )}
          </GroupResult>
        </div>
      </li>
    );
  };

  const recentRow = (r) => {
    const mergedIds = (Array.isArray(r.mergedIds) ? r.mergedIds : []).map(String);
    const label = shortLabel(r);
    return (
      <li key={r.id} className="merge-row merge-row-recent">
        <div className="merge-row-main">
          <div className="merge-label">
            {label} ({r.quantity ?? '?'}){mergedIds.length > 0 && ` → ${idList(mergedIds)}`}
          </div>
          <div className="merge-sources">
            {r.by ? `by ${r.by} · ` : ''}{formatDateTimeEastern(r.createdAt)}
          </div>
        </div>
        <div className="merge-result" aria-live="polite">
          <UndoControl undo={undos[String(r.id)]} label={label} disabled={busy} onUndo={() => handleUndo(r.id, mergedIds, label)} />
        </div>
      </li>
    );
  };

  const retryButton = (
    <button type="button" className="btn-secondary btn-sm" onClick={() => refetch()} disabled={isFetching}>
      {isFetching ? 'Retrying…' : 'Retry'}
    </button>
  );
  const rows = displayGroups(groups, ran, results);

  let planBody;
  if (isLoading) {
    planBody = <div className="merge-empty">Loading merge plan…</div>;
  } else if (isError && !data) {
    planBody = (
      <div className="ai-inline-error merge-empty">
        Could not load the merge plan: {planErrorText(error)} {retryButton}
      </div>
    );
  } else if (!rows.length) {
    planBody = <div className="merge-empty">No listings on this event can be merged.</div>;
  } else {
    planBody = (
      <>
        <label className="merge-select-all">
          <input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={!runnable.length || busy} />
          Select all ({runnable.length} can merge)
        </label>
        <ul className="merge-list">{rows.map(groupRow)}</ul>
      </>
    );
  }

  let stopText = null;
  if (stop?.kind === 'rate_limited') stopText = <>Rate limited: the rest were not sent. <RetryCountdown until={stop.retryAt} /></>;
  else if (stop?.kind === 'unavailable') stopText = <>{stop.message} The rest were not sent.</>;
  else if (stop?.kind === 'unknown') stopText = <>{UNKNOWN_OUTCOME_TEXT} Close this window to refresh; the rest were not sent.</>;

  return (
    <div className="modal-overlay" onClick={busy ? undefined : handleClose}>
      <div
        ref={dialogRef}
        className="modal modal-content merge-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ maxWidth: 860, width: '95%' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId} style={{ margin: 0 }}>Merge listings ({groups.length})</h2>
          <button className="modal-close" onClick={handleClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <div className="ai-admin-note" style={{ marginBottom: 8 }}>{RULES_NOTE}</div>
          {isError && data && (
            <div className="ai-inline-error merge-refresh-error">
              Could not refresh the plan (showing the last one): {planErrorText(error)} {retryButton}
            </div>
          )}
          {planBody}
          <div aria-live="polite">
            {stopText && <div className="ai-inline-error" style={{ marginTop: 10 }}>{stopText}</div>}
          </div>
          {blocked.length > 0 && (
            <details className="merge-blocked">
              <summary>{blocked.length} side-by-side pair{blocked.length === 1 ? '' : 's'} not merged</summary>
              <ul>
                {blocked.map((b, i) => (
                  <li key={`${(b.ids || []).join('-')}:${i}`} title={Array.isArray(b.ids) ? idList(b.ids) : undefined}>
                    {blockedLabel(b)}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {recent.length > 0 && (
            <>
              <div className="merge-subhead">Recent merges</div>
              <ul className="merge-list">{recent.map(recentRow)}</ul>
            </>
          )}
        </div>
        <div className="modal-footer bulk-review-footer">
          <span className="ai-admin-note">
            {selected.length} selected
            {mergedCount > 0 && ` · ${mergedCount} merged`}
            {failedCount > 0 && ` · ${failedCount} failed`}
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn-secondary" onClick={handleClose} disabled={busy}>Close</button>
          <button
            className="btn-primary"
            onClick={handleMerge}
            disabled={busy || locked || isFetching || !selected.length}
            title={locked ? UNKNOWN_OUTCOME_TEXT : undefined}
          >
            {running ? 'Merging…' : `Merge ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MergeListingsModal;
